/**
 * Stack, VStack, HStack Components
 * Flex-based stack layouts with automatic spacing
 */

import React, { forwardRef, Children, cloneElement, isValidElement } from 'react';
import { Box } from './Box';
import { Flex } from './Flex';

/**
 * Stack Component
 * Arranges children in a stack with consistent spacing
 * 
 * @example
 * <Stack spacing={4} direction="column">
 *   <Box>Item 1</Box>
 *   <Box>Item 2</Box>
 * </Stack>
 */
export const Stack = forwardRef(({
  direction = 'column',
  align,
  justify,
  spacing = 0,
  wrap,
  divider,
  shouldWrapChildren,
  children,
  ...props
}, ref) => {
  const validChildren = Children.toArray(children).filter(isValidElement);

  const clones = validChildren.map((child, index) => {
    const isLast = index === validChildren.length - 1;
    
    // Add spacing between items
    const spacingStyle = direction === 'column' || direction === 'column-reverse'
      ? { marginBottom: isLast ? undefined : spacing }
      : { marginRight: isLast ? undefined : spacing };

    // Wrap children if specified
    const wrapped = shouldWrapChildren ? (
      <Box display="inline-block" {...spacingStyle}>
        {child}
      </Box>
    ) : (
      cloneElement(child, {
        ...child.props,
        style: {
          ...child.props.style,
          ...(!isLast ? spacingStyle : {}),
        },
      })
    );

    return (
      <React.Fragment key={child.key || index}>
        {wrapped}
        {divider && !isLast && (
          <Box
            mx={direction === 'row' || direction === 'row-reverse' ? spacing : undefined}
            my={direction === 'column' || direction === 'column-reverse' ? spacing : undefined}
          >
            {divider}
          </Box>
        )}
      </React.Fragment>
    );
  });

  return (
    <Flex
      ref={ref}
      direction={direction}
      align={align}
      justify={justify}
      wrap={wrap}
      gap={divider ? undefined : spacing}
      {...props}
    >
      {divider ? clones : children}
    </Flex>
  );
});

Stack.displayName = 'Stack';

/**
 * HStack Component
 * Horizontal stack (Stack with direction="row")
 * 
 * @example
 * <HStack spacing={4}>
 *   <Box>Item 1</Box>
 *   <Box>Item 2</Box>
 * </HStack>
 */
export const HStack = forwardRef(({ spacing = 2, ...props }, ref) => {
  return (
    <Stack
      ref={ref}
      direction="row"
      align="center"
      spacing={spacing}
      {...props}
    />
  );
});

HStack.displayName = 'HStack';

/**
 * VStack Component
 * Vertical stack (Stack with direction="column")
 * 
 * @example
 * <VStack spacing={4}>
 *   <Box>Item 1</Box>
 *   <Box>Item 2</Box>
 * </VStack>
 */
export const VStack = forwardRef(({ spacing = 2, ...props }, ref) => {
  return (
    <Stack
      ref={ref}
      direction="column"
      align="center"
      spacing={spacing}
      {...props}
    />
  );
});

VStack.displayName = 'VStack';

export default Stack;
